import classNames from "classnames";
import { FC } from "react";

import { ReactComponent as InfoIcon } from "assets/images/common/info.svg";

import HistoricalChart, { ChartRefLine, ChartSeries } from "modules/assets/HistoricalChart";

import styles from "./style.module.scss";

export type ChartCaptionSegment = { text: string; tone?: "red" | "cyan" };

export type LegendSensor = { deviceId: string; location: string; color: string };

export type HistoricalChartData = {
  id: string;
  title: string;
  unit: string;
  info?: string;
  badge: string;
  emptyState: null | "no-sensors" | "no-data";
  series: ChartSeries[];
  sensors: LegendSensor[];
  yMin: number;
  yMax: number;
  yTicks: number[];
  refLines: ChartRefLine[];
  threshold: number | null;
  captions: ChartCaptionSegment[][];
  thresholdNote?: string;
  valueUnit: string;
  pointTimes: string[];
  xLabels: { text: string; at: number }[];
  peakLabel: string;
  format: (value: number) => string;
};

const EMPTY_TEXT: Record<"no-sensors" | "no-data", string> = {
  "no-sensors": "No sensors measuring this metric",
  "no-data": "No data for the selected period",
};

type PropTypes = {
  data: HistoricalChartData;
  className?: string;
};

const HistoricalChartCard: FC<PropTypes> = ({ data, className }) => {
  const legend: LegendSensor[] = data.series.length > 0 ? data.series : data.sensors;

  return (
    <article className={classNames(styles.card, className)}>
      <header className={styles.head}>
        <h3 className={styles.title}>
          {data.title} <span className={styles.unit}>{data.unit}</span>
          {data.info && (
            <span className={styles.info} tabIndex={0}>
              <InfoIcon />
              <span className={styles.infoTooltip}>{data.info}</span>
            </span>
          )}
        </h3>

        <span className={styles.badge}>{data.badge}</span>
      </header>

      {data.emptyState ? (
        <div className={styles.empty}>{EMPTY_TEXT[data.emptyState]}</div>
      ) : (
        <HistoricalChart
          series={data.series}
          yMin={data.yMin}
          yMax={data.yMax}
          yTicks={data.yTicks}
          peakLabel={data.peakLabel}
          pointTimes={data.pointTimes}
          xLabels={data.xLabels}
          valueUnit={data.valueUnit}
          format={data.format}
          refLines={data.refLines}
          threshold={data.threshold}
          thresholdNote={data.thresholdNote}
        />
      )}

      <div className={styles.captions}>
        {data.captions.map((line, lineIndex) => (
          <p key={lineIndex} className={styles.caption}>
            {line.map((segment, segmentIndex) => (
              <span
                key={segmentIndex}
                className={classNames(
                  segment.tone === "red" && styles.red,
                  segment.tone === "cyan" && styles.cyan,
                )}
              >
                {segment.text}
              </span>
            ))}
          </p>
        ))}
      </div>

      {data.emptyState !== "no-sensors" && legend.length > 0 && (
        <ul className={styles.legend}>
          {legend.map((item, index) => (
            <li key={index} className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: item.color }} />

              <span className={styles.legendText}>
                <span className={styles.legendDevice}>Device: {item.deviceId}</span>
                <span className={styles.legendLoc}>Location: {item.location}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
};

export default HistoricalChartCard;
